import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Client, CreateClientData, UpdateClientData } from '../../types';

interface ClientFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateClientData | UpdateClientData) => Promise<void>;
  client?: Client;
  loading?: boolean;
  userRole?: string; // Add user role to control percentage field access
  canCreateLogin?: boolean; // Allow creating a CLIENT login inline during creation
}

// Country list
const countries = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France',
  'Spain', 'Italy', 'Netherlands', 'Brazil', 'Mexico', 'India', 'Japan', 'China',
  'South Korea', 'Singapore', 'UAE', 'South Africa', 'Argentina', 'Chile',
  'Sweden', 'Norway', 'Denmark', 'Finland', 'Belgium', 'Switzerland', 'Austria',
  'Portugal', 'Ireland', 'New Zealand', 'Thailand', 'Philippines', 'Malaysia',
  'Indonesia', 'Vietnam', 'Poland', 'Czech Republic', 'Hungary', 'Romania',
  'Bulgaria', 'Croatia', 'Slovenia', 'Slovakia', 'Lithuania', 'Latvia', 'Estonia'
];

const ClientForm: React.FC<ClientFormProps> = ({
  open,
  onClose,
  onSubmit,
  client,
  loading = false,
  userRole = 'ADMIN',
  canCreateLogin = false,
}) => {
  const showBudget = userRole === 'SUPER_ADMIN' || userRole === 'AGENCY';
  const [formData, setFormData] = useState({
    name: client?.name || '',
    email: client?.email || '',
    phone: client?.phone || '',
    company: client?.company || '',
    country: client?.country || '',
    percentage: client?.percentage || 100,
    budget: client?.budget?.toString() || '',
    status: client?.status || 'ACTIVE',
    createLogin: false,
    loginPassword: '',
  });

  // Keep local form state in sync when dialog opens or selected client changes
  useEffect(() => {
    setFormData({
      name: client?.name || '',
      email: client?.email || '',
      phone: client?.phone || '',
      company: client?.company || '',
      country: client?.country || '',
      percentage: client?.percentage || 100,
      budget: client?.budget?.toString() || '',
      status: client?.status || 'ACTIVE',
      createLogin: false,
      loginPassword: '',
    });
  }, [client, open]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: string | boolean) => {
    let processedValue: any = value;
    
    // Convert percentage to number
    if (field === 'percentage' && typeof value === 'string') {
      processedValue = value ? parseInt(value, 10) : 10;
    }
    
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    // Optional: basic phone validation when present
    if (formData.phone && !/^[- +()0-9]{7,20}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number';
    }

    // Percentage validation (only for super admin)
    if (userRole === 'SUPER_ADMIN') {
      if (formData.percentage < 10) {
        newErrors.percentage = 'Percentage must be at least 10%';
      } else if (formData.percentage % 5 !== 0) {
        newErrors.percentage = 'Percentage must be in increments of 5';
      }
    }

    // Inline login validation (only when creating a new client + toggle on)
    if (!client && canCreateLogin && formData.createLogin) {
      if (!formData.loginPassword || formData.loginPassword.length < 6) {
        newErrors.loginPassword = 'Password must be at least 6 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Align payload with backend schema (see backend/src/routes/clients.ts)
      // Prisma Client model `Client` supports: name, email, phone?, company?, country?, address?, billingEmail?, currency?, percentage?, isActive?
      const payload: any = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        company: formData.company || undefined,
        country: formData.country || undefined,
      };

      // Only super admin can set the markup percentage
      if (userRole === 'SUPER_ADMIN') {
        payload.percentage = parseInt(formData.percentage.toString(), 10);
      }

      // Super admin and agencies can set a client budget
      if (showBudget && formData.budget) {
        payload.budget = parseFloat(formData.budget);
      }

      // Inline login creation (only when creating a new client)
      if (!client && canCreateLogin && formData.createLogin) {
        payload.createLogin = true;
        payload.loginPassword = formData.loginPassword;
      }

      // For updates, convert UI status to boolean isActive
      if (client) {
        payload.isActive = formData.status === 'ACTIVE';
      }

      await onSubmit(payload);
      onClose();
    } catch (error: any) {
      console.error('Form submission error:', error);
      
      // Handle unique email constraint violation
      if (error.response?.status === 409 && error.response?.data?.field === 'email') {
        setErrors(prev => ({
          ...prev,
          email: error.response.data.error || 'A client with this email already exists'
        }));
      } else if (error.response?.data?.errors) {
        // Handle validation errors from express-validator
        const newErrors: Record<string, string> = {};
        error.response.data.errors.forEach((err: any) => {
          newErrors[err.path || err.param] = err.msg;
        });
        setErrors(newErrors);
      }
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      country: '',
      percentage: 100,
      budget: '',
      status: 'ACTIVE',
      createLogin: false,
      loginPassword: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      BackdropProps={{
        style: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--border)',
          maxWidth: '580px',
          animation: 'modalSlideIn 0.3s ease-out',
          '@keyframes modalSlideIn': {
            from: {
              opacity: 0,
              transform: 'scale(0.9)'
            },
            to: {
              opacity: 1,
              transform: 'scale(1)'
            }
          }
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '22px 26px 18px',
          borderBottom: '1px solid var(--border-light)'
        }}>
          <Box>
            <DialogTitle sx={{ padding: 0, fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.3px' }}>
              {client ? 'Edit Client' : 'Add New Client'}
            </DialogTitle>
            <Box sx={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '2px' }}>
              Fill in the details below to {client ? 'update the' : 'create a new'} client account
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              width: '30px',
              height: '30px',
              borderRadius: '6px',
              '&:hover': {
                background: 'var(--bg)'
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }}>
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </IconButton>
        </Box>
        <DialogContent sx={{ padding: '22px 26px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box component="label" sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Name <span style={{ color: 'var(--red)', marginLeft: '2px' }}>*</span>
              </Box>
              <TextField
                fullWidth
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                placeholder="e.g. John Smith"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '13px',
                    '& input': {
                      padding: '9px 12px'
                    },
                    '&:hover fieldset': {
                      borderColor: 'var(--border)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--accent)',
                      boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '11.5px',
                    marginLeft: 0,
                    marginTop: '2px'
                  }
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box component="label" sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Email <span style={{ color: 'var(--red)', marginLeft: '2px' }}>*</span>
              </Box>
              <TextField
                fullWidth
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                placeholder="e.g. john@company.com"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '13px',
                    '& input': {
                      padding: '9px 12px'
                    },
                    '&:hover fieldset': {
                      borderColor: 'var(--border)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--accent)',
                      boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '11.5px',
                    marginLeft: 0,
                    marginTop: '2px'
                  }
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box component="label" sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Phone
              </Box>
              <TextField
                fullWidth
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                placeholder="e.g. +1 555 000 0000"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '13px',
                    '& input': {
                      padding: '9px 12px'
                    },
                    '&:hover fieldset': {
                      borderColor: 'var(--border)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--accent)',
                      boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '11.5px',
                    marginLeft: 0,
                    marginTop: '2px'
                  }
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box component="label" sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Company
              </Box>
              <TextField
                fullWidth
                value={formData.company}
                onChange={(e) => handleChange('company', e.target.value)}
                placeholder="e.g. Acme Inc."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '13px',
                    '& input': {
                      padding: '9px 12px'
                    },
                    '&:hover fieldset': {
                      borderColor: 'var(--border)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--accent)',
                      boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                    }
                  }
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box component="label" sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Country
              </Box>
              <Select
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '13px',
                  '& .MuiSelect-select': {
                    padding: '9px 12px'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--border)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--accent)',
                    boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                  }
                }}
              >
                <MenuItem value="" sx={{ fontSize: '13px' }}>Select country…</MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country} value={country} sx={{ fontSize: '13px' }}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {/* Budget field - visible to super admin and agencies */}
            {showBudget && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Box component="label" sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                  Client Budget ($)
                </Box>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.budget}
                  onChange={(e) => handleChange('budget', e.target.value)}
                  error={!!errors.budget}
                  helperText={errors.budget}
                  placeholder="0.00"
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '13px',
                      '& input': {
                        padding: '9px 12px'
                      },
                      '&:hover fieldset': {
                        borderColor: 'var(--border)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--accent)',
                        boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '11.5px',
                      color: 'var(--text-muted)',
                      marginLeft: 0,
                      marginTop: '2px'
                    }
                  }}
                />
                <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Optional — total budget allocated for this client
                </Box>
              </Box>
            )}
            {/* Percentage field - only visible to super admin */}
            {userRole === 'SUPER_ADMIN' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', gridColumn: '1 / -1' }}>
                <Box component="label" sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                  Client Percentage (%)
                </Box>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.percentage}
                  onChange={(e) => handleChange('percentage', e.target.value)}
                  error={!!errors.percentage}
                  helperText={errors.percentage}
                  inputProps={{ min: 10, step: 5 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '13px',
                      '& input': {
                        padding: '9px 12px'
                      },
                      '&:hover fieldset': {
                        borderColor: 'var(--border)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--accent)',
                        boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '11.5px',
                      color: 'var(--text-muted)',
                      marginLeft: 0,
                      marginTop: '2px'
                    }
                  }}
                />
                <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Markup percentage for guest blog sites (min 10%, increments of 5)
                </Box>
              </Box>
            )}
            {client && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Box component="label" sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                  Status
                </Box>
                <Select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  sx={{
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '13px',
                    '& .MuiSelect-select': {
                      padding: '9px 12px'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--border)'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--accent)',
                      boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                    }
                  }}
                >
                  <MenuItem value="ACTIVE" sx={{ fontSize: '13px' }}>Active</MenuItem>
                  <MenuItem value="INACTIVE" sx={{ fontSize: '13px' }}>Inactive</MenuItem>
                </Select>
              </Box>
            )}
            {!client && canCreateLogin && (
              <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '10px', pt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.createLogin}
                      onChange={(e) => handleChange('createLogin', e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Box sx={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>
                        Create client login
                      </Box>
                      <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
                        Uses the name and email above so your client can sign in at /login
                      </Box>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', m: 0 }}
                />
                {formData.createLogin && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', pl: 4 }}>
                    <Box component="label" sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                      Login password <span style={{ color: 'var(--red)' }}>*</span>
                    </Box>
                    <TextField
                      fullWidth
                      type="password"
                      value={formData.loginPassword}
                      onChange={(e) => handleChange('loginPassword', e.target.value)}
                      error={!!errors.loginPassword}
                      helperText={errors.loginPassword || 'Minimum 6 characters — share this with your client'}
                      placeholder="Set a password for client login"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '13px',
                          '& input': { padding: '9px 12px' },
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '10px',
          padding: '16px 26px',
          borderTop: '1px solid var(--border-light)'
        }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            sx={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              border: '1px solid var(--border)',
              color: 'var(--text-secondary)',
              backgroundColor: 'var(--surface)',
              '&:hover': {
                backgroundColor: 'var(--bg)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            startIcon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            }
            sx={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              backgroundColor: 'var(--accent)',
              color: 'white',
              boxShadow: '0 1px 2px rgba(37,99,235,0.2)',
              '&:hover': {
                backgroundColor: 'var(--accent-hover)',
                boxShadow: 'var(--shadow-md)'
              }
            }}
          >
            {client ? 'Update Client' : 'Create Client'}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default ClientForm;
